import { Link } from 'react-router-dom';
import usePageMeta from '../lib/usePageMeta';
import Hero from '../components/Hero';
import About from '../components/About';
import WhyUs from '../components/WhyUs';
import Testimonials from '../components/Testimonials';
import ProjectCard from '../components/ProjectCard';
import Icon from '../components/Icon';
import { StatsBand, Approach, Gallery, TeamStrip, CtaBanner } from '../components/Sections';
import { SectionHead, ServiceGrid, TechMarquee, LatestPosts, IndustryChips, FaqList } from '../components/Blocks';
import { PROJECTS, PROCESS, FAQS } from '../data';
import { IMG, u } from '../images';

export default function Home() {
  usePageMeta('', 'Origo Private Limited — web and mobile development, UI/UX design, branding, 2D/3D animation and digital marketing from one expert team.');

  return (
    <>
      <Hero />
      <About />

      <section className="section bg-brand-soft">
        <div className="container-x">
          <SectionHead eyebrow="What we do" title="Everything your brand needs to go digital" text="Development, design, animation and marketing — twelve core services under one roof, so nothing gets lost between agencies." />
          <div className="mt-14"><ServiceGrid limit={8} /></div>
          <div className="mt-12 text-center" data-anim="up">
            <Link to="/services" className="btn-green">View all 12 services <Icon name="arrow" className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      <StatsBand />
      <Approach />
      <WhyUs />

      <section className="section bg-grad-soft">
        <div className="container-x">
          <SectionHead eyebrow="How we work" title="From idea to launch in five clear steps" text="A simple, transparent process with a review at the end of each phase." />
          <ol className="mt-14 grid gap-5 md:grid-cols-5" data-anim="up" data-stagger>
            {PROCESS.map((s, i) => (
              <li key={s.n} className="lift group overflow-hidden rounded-3xl border border-brand/10 bg-brand-soft/60">
                <div className="relative h-36 overflow-hidden">
                  <img src={u(IMG.process[i % IMG.process.length], 500)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent" />
                  <span className="absolute bottom-2 left-4 font-display text-4xl font-bold text-brand-lime">{s.n}</span>
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-ink">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-10 text-center" data-anim="up"><Link to="/process" className="btn-outline">See our full process</Link></div>
        </div>
      </section>

      <section className="section bg-brand-soft">
        <div className="container-x">
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div className="max-w-2xl" data-anim="left">
              <span className="eyebrow">Our work</span>
              <h2 className="h2 mt-5 text-ink">Selected <span className="text-accent">projects</span> we are <span className="text-outline">proud</span> of</h2>
            </div>
            <Link to="/portfolio" className="btn-outline self-start lg:self-auto" data-anim="right">View all projects <Icon name="arrow" className="h-4 w-4" /></Link>
          </div>
          <div className="mt-12 grid gap-7 sm:grid-cols-2 lg:grid-cols-3" data-anim="zoom" data-stagger>
            {PROJECTS.slice(0, 3).map((p, i) => <ProjectCard key={p.slug} project={p} index={i} />)}
          </div>
          <div className="mt-20"><TechMarquee /></div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-x">
          <SectionHead eyebrow="Industries" title="Experience across the sectors that matter" text="Domain knowledge shortens discovery, avoids costly mistakes and speeds up delivery." />
          <div className="mt-10"><IndustryChips /></div>
        </div>
      </section>

      <Gallery />
      <TeamStrip />
      <Testimonials />

      <section className="section bg-brand-soft">
        <div className="container-x">
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div className="max-w-2xl" data-anim="left">
              <span className="eyebrow">Insights</span>
              <h2 className="h2 mt-5 text-ink"><span className="text-accent">Latest</span> from our <span className="text-outline">blog</span></h2>
            </div>
            <Link to="/blog" className="btn-outline self-start lg:self-auto" data-anim="right">All articles <Icon name="arrow" className="h-4 w-4" /></Link>
          </div>
          <div className="mt-12"><LatestPosts /></div>
        </div>
      </section>

      <CtaBanner />

      <section className="section bg-grad-soft">
        <div className="container-x grid gap-14 lg:grid-cols-[.8fr_1.2fr]">
          <div data-anim="left">
            <span className="eyebrow">FAQ</span>
            <h2 className="h2 mt-5 text-ink">Questions we <span className="text-accent">hear</span> <span className="text-outline">a lot</span></h2>
            <p className="mt-5 text-lg text-slate-600">Cannot find what you are looking for? Our team replies within one business day.</p>
            <Link to="/contact" className="btn-green mt-8">Ask us anything <Icon name="arrow" className="h-4 w-4" /></Link>
          </div>
          <FaqList items={FAQS.slice(0, 5)} />
        </div>
      </section>
    </>
  );
}
